import { HeroLeadGenerationSection } from "components/lead-generation-v2/hero-lead-generation-section/hero-lead-generation-section";
import useEligibleSlotSelection from "hooks/pages/use-eligible-slot-selection/use-eligible-slot-selection";

import { useRouter } from "next/router";
import React, { useEffect } from "react";
import { BimbelHomeSEO } from "seo/bimbel.seo";
import { useAuth } from "stores/auth/auth-context";
import { PageLoader } from "styles/common/pageLoader";
import { LoadingSpinner } from "../components/icons/loading-spinner";
import { useKelasLiveContext } from "stores/kelas-live/kelas-live-context";
import { SubscriptionType } from "services/api/sessions/sessionService.types";
import { onlineAdvantageInfoCard } from "data/online-class-advantage";
import { InfoCard } from "components/info-card/info-card";
import { MoneyBackBanner } from "components/moneyback-banner/moneyback-banner";
import { scheduleCard } from "data/schedule";
import { ExploreTeachers } from "components/explore-teachers/explore-teachers";
import { Social } from "components/social/social";
import { useBoolean } from "hooks/use-boolean/use-boolean";
import dynamic from "next/dynamic";
import { useTanyaContext } from "stores/tanya/tanya-context";
import { getLeadSourceFromPath } from "utils/utils";

const Bimbel: React.FC = () => {
  const {
    user: { isUserLoggedIn, userData }
  } = useAuth();
  useBoolean(false);
  const { contactMeForm } = useTanyaContext();
  const router = useRouter();
  const leadSource = getLeadSourceFromPath(router.pathname);

  const { userSubscription } = useKelasLiveContext();

  const [showLeadGenerationModal, setShowLeadGenerationModal] =
    useBoolean(false);

  const { utm_campaign, utm_source, utm_medium, utm_term, utm_content } =
    router.query;
  const subscriptionType =
    userSubscription && userSubscription?.subscriptionStatus?.subscriptionType;

  const { isEligibleSlotSelection, eligibility } = useEligibleSlotSelection();
  const ContactMe = dynamic(() => import("components/contact-me/contact-me"));
  const LeadGenerationModal = dynamic(
    () =>
      import(
        "components/lead-generation/lead-generation-modal/lead-generation-modal"
      )
  );

  useEffect(() => {
    if (
      isUserLoggedIn &&
      userData?.primary_user &&
      userData?.primary_user?.profile_state === "INCOMPLETE"
    ) {
      router.push({
        pathname: "/user/register/add-location",
        query: router.query
      });
    }
  }, [isUserLoggedIn, userData?.primary_user?.profile_state]);

  // Ensure subscription status is loaded on this page itself instead of relying
  // on the header component to trigger the fetch.
  useEffect(() => {
    const token = userData?.primary_user?.access_token;
    if (token) userSubscription?.fetchSubscriptionStatus?.(token);
  }, [userData?.primary_user?.access_token]);

  useEffect(() => {
    if (
      eligibility &&
      subscriptionType &&
      subscriptionType !== SubscriptionType.FREE
    ) {
      const { courseIds, userSubscriptionId } = eligibility || {};
      if (
        !isEligibleSlotSelection &&
        !courseIds?.length &&
        !userSubscriptionId
      ) {
        router.push("/bimbel/user");
        return;
      }
      const searchParams = new URLSearchParams({
        userSubscriptionId,
        currentStep: "1"
      });
      courseIds.forEach((id) => searchParams.append("courseIds[]", id));
      router.push({
        pathname: "/bimbel/slot-selection",
        search: searchParams.toString()
      });
    }
  }, [isEligibleSlotSelection, eligibility, router, subscriptionType]);

  if (isUserLoggedIn && subscriptionType !== SubscriptionType.FREE) {
    return (
      <PageLoader>
        <LoadingSpinner />
      </PageLoader>
    );
  }

  return (
    <>
      <BimbelHomeSEO />
      <HeroLeadGenerationSection
        onPriceInfoCTAClick={() => setShowLeadGenerationModal.on()}
        source="Banner"
      />
      {onlineAdvantageInfoCard.map((card, index) => (
        <InfoCard
          key={index}
          {...card}
          backgroundColor="#FFFFFF"
          boldStyle={{
            fontWeight: "900",
            fontSize: "28px",
            fontFamily: "Poppins"
          }}
        />
      ))}
      <ExploreTeachers />
      {scheduleCard.map((card, index) => (
        <InfoCard key={index} {...card} backgroundColor="#ECF6FF" />
      ))}

      <Social />
      <MoneyBackBanner />
      {contactMeForm.show && (
        <ContactMe
          metaData={contactMeForm.metaData}
          onClose={() => contactMeForm.setContactMeFormData({ show: false })}
          leadSource={leadSource}
        />
      )}
      {showLeadGenerationModal && (
        <LeadGenerationModal
          onClose={() => setShowLeadGenerationModal.off()}
          onProceed={() => {
            setShowLeadGenerationModal.off();
            contactMeForm.setContactMeFormData({
              show: true,
              metaData: {
                utm_campaign,
                utm_content,
                utm_medium,
                utm_source,
                utm_term
              }
            });
          }}
        />
      )}
    </>
  );
};

export default Bimbel;
